/**
 * QHS UnsavedChangesGuard
 * -----------------------
 * Drop-in replacement for the legacy
 * `_legacy/components/Shared/UnsavedChangesGuard`. Same prop shape so
 * pages can swap the import without touching anything else, but the
 * confirmation dialog renders the v3 redesigned `UnsavedChangesModal`
 * instead of the legacy MUI dialog.
 *
 *   <UnsavedChangesGuard
 *     hasUnsavedChanges={dirty}
 *     isSaving={isSubmitting}
 *     title={unsavedChanges.title}
 *     message={unsavedChanges.orderEdit}
 *     discardButtonText={unsavedChanges.leaveAnyway}
 *     keepEditingButtonText={unsavedChanges.keepEditing}
 *   >
 *     <Form />
 *   </UnsavedChangesGuard>
 *
 * Underlying nav-blocking logic stays delegated to
 * `useUnsavedChangesSimple` (popstate guard + beforeunload warning) —
 * this component only wires the hook to the redesigned modal.
 */
import useUnsavedChangesSimple from '../../hooks/useUnsavedChangesSimple';
import UnsavedChangesModal from '../UnsavedChangesModal';

const UnsavedChangesGuard = ({
  hasUnsavedChanges,
  // A save in flight. Kept apart from `hasUnsavedChanges` on purpose: the
  // two are combined differently for the navigation guard and for the
  // unsaved-work registry, and a caller that pre-combines them
  // (`dirty && !isSubmitting`) silently gets the registry wrong.
  isSaving = false,
  children,
  title,
  message,
  discardButtonText,
  keepEditingButtonText,
  enableBrowserWarning = true,
}) => {
  const { showModal, handleKeepEditing, handleDiscardChanges } = useUnsavedChangesSimple(
    hasUnsavedChanges,
    { enableBrowserWarning, isSaving }
  );

  return (
    <>
      {children}
      <UnsavedChangesModal
        open={showModal}
        onDiscard={handleDiscardChanges}
        onKeepEditing={handleKeepEditing}
        title={title}
        message={message}
        discardLabel={discardButtonText}
        keepEditingLabel={keepEditingButtonText}
      />
    </>
  );
};

export default UnsavedChangesGuard;
